// Combat (§2.5, §2.6, §2.7, §3.3, §20.9): the action dispatch of the input phase (lunge, grab,
// throw, escape), timed attack states, and the combat resolution phase (clashes, shove and punch
// hits against lag-compensated targets, grab checks), plus the impulse API shared by attacks,
// items and hazards (`applyKnockback`, `applyBlast`, `applyBlastAt`).
//
// Predict mode (§18.2): combat resolution is skipped (no hits, clashes or grabs); the local
// bean's GRAB_START waits for the server's outcome (carry.ts). `applyKnockback` and the blasts are
// server-only no-ops.
import { MAX_BEANS, NO_SLOT, TUNE } from '../constants';
import { EV, emit } from '../events';
import { BS, Cause, EK } from '../ids';
import { MODES } from '../modes/index';
import { DEG, clampLength, lerp, v2 } from '../math';
import type { Bean, InputFrame, StepContext, Vec3, World } from '../types';
import { popFloat } from './balloon';
import { countEscape, freeFromCarrier, releaseCarry, resolveGrabs, startGrab, throwCarried } from './carry';
import { dcos, dsin } from './dmath';
import { forceFall, isGrounded } from './edges';
import { removeEntityAt } from './entities';
import { assistedDir, type PressSet } from './input';
import { positionAt } from './lagcomp';
import { isProp, knockProp, propHidden, propRadius } from './props';
import { STATE_TABLE, isHittable } from './stateTable';
import { isPredicting, isTeammate, refreshTouch, setTouch } from './world';
import { breakYank } from './yank';

export interface KnockOpts { dizzy: number; cause: Cause; by: number; scaleByDizzy: boolean; vz?: number; noTumble?: boolean; hazard?: boolean; teammateCheck?: boolean }

/** Every knockback scales with the victim's Dizzy (§2.7): 2.2× at 100. */
export function dizzyMult(dizzy: number): number {
  return 1 + TUNE.dizzy.kbPerPoint * dizzy;
}

/** Adds a Dizzy gain (× the round's gain mult, §4.6 DOUBLE DIZZY), capped at the max. */
export function addDizzy(w: World, b: Bean, amount: number): void {
  b.dizzy = Math.min(TUNE.dizzy.max, b.dizzy + amount * w.rules.dizzyGainMult);
}

const SHOVE_COS = dcos(TUNE.shove.halfArcDeg * DEG);
const PUNCH_COS = dcos(TUNE.glove.halfArcDeg * DEG);
/** Lunge hit-mask bit of prop `data` index; bean slots use bits 0–7. */
const PROP_MASK_SHIFT = 8;
/** Clash knockbacks closer than this are even (no winner). */
const CLASH_EVEN_EPS = 1e-6;

const rewound: Vec3 = { x: 0, y: 0, z: 0 };
const dir = v2();

/** Lunge start (§2.5): along the assisted attack vector when valid, else facing; cooldown from lunge start. */
export function startLunge(w: World, b: Bean, frame: InputFrame | null): void {
  assistedDir(b.facing, frame ? frame.sx : 0, frame ? frame.sy : 0, b.lungeDir);
  b.state = BS.LUNGE;
  b.st = 0;
  b.lungeStartTick = w.tick;
  b.lungeHitMask = 0;
  b.cdShove = TUNE.shove.cooldownTicks;
  emit(w, EV.LUNGE, b.slot);
}

/**
 * Input phase (§18.1 step 3): Shove lunges (NORMAL) or drops the carried victim in front and then
 * lunges (CARRYING), both only off cooldown; Grab starts a grab (NORMAL, grounded) or throws
 * (CARRYING); a hold that reaches its limit auto-throws along facing; a GRABBED victim's presses
 * count toward escape.
 */
export function beginActions(w: World, b: Bean, p: PressSet, frame: InputFrame | null): void {
  const row = STATE_TABLE[b.state];
  if (row.shove === 'yes' && p.shove && b.cdShove === 0) {
    startLunge(w, b, frame);
    return;
  }
  if (row.shove === 'drop' && p.shove && b.cdShove === 0) {
    releaseCarry(w, b, 'place');
    b.state = BS.NORMAL;
    startLunge(w, b, frame);
    return;
  }
  if (row.grab === 'yes') {
    if (p.grab) startGrab(w, b, frame);
    return;
  }
  if (row.grab === 'throw') {
    if (p.grab) throwCarried(w, b, frame ? frame.ax : 0, frame ? frame.ay : 0);
    else if (b.st >= TUNE.grab.holdTicks) throwCarried(w, b, 0, 0);
    return;
  }
  if (row.grab === 'mash') countEscape(w, b, p.escape);
}

/** Ends an attack or slide state into NORMAL. A lunge that ends (for any reason) clamps to run speed (§2.5). */
export function endToNormal(b: Bean): void {
  if (b.state === BS.LUNGE) clampLength(b.vel, b.vel.x, b.vel.y, TUNE.bean.runSpeed);
  b.state = BS.NORMAL;
  b.st = 0;
}

/**
 * Timer phase: timed exits of attack and slide states. LUNGE (9 ticks) returns to NORMAL clamped
 * to run speed; GRAB_MISS (21), PUNCH (18) and SLIP (60) return to NORMAL; PUNCH_WINDUP (15) becomes
 * PUNCH along the facing it has turned to.
 */
export function tickCombatState(w: World, b: Bean): void {
  switch (b.state) {
    case BS.LUNGE:
      if (b.st >= TUNE.shove.lungeTicks) endToNormal(b);
      break;
    case BS.GRAB_MISS:
      if (b.st >= TUNE.grab.missTicks) endToNormal(b);
      break;
    case BS.SLIP:
      if (b.st >= TUNE.banana.slipTicks) endToNormal(b);
      break;
    case BS.PUNCH_WINDUP:
      if (b.st >= TUNE.glove.windupTicks) {
        b.state = BS.PUNCH;
        b.st = 0;
        b.lungeDir.x = dcos(b.facing);
        b.lungeDir.y = dsin(b.facing);
        b.lungeHitMask = 0;
        emit(w, EV.PUNCH, b.slot);
      }
      break;
    case BS.PUNCH:
      if (b.st >= TUNE.glove.punchTicks) endToNormal(b);
      break;
    default:
      break;
  }
}

/** Target position for attacker hit tests: rewound by `rewind` ticks unless that looks like a teleport (§20.9). */
export function hitTestPosition(w: World, t: Bean, rewind: number): Vec3 {
  positionAt(w, t.slot, rewind, rewound);
  if (Math.hypot(rewound.x - t.pos.x, rewound.y - t.pos.y, rewound.z - t.z) > TUNE.lagcomp.teleportDist) {
    rewound.x = t.pos.x;
    rewound.y = t.pos.y;
    rewound.z = t.z;
  }
  return rewound;
}

/** Attack knockback direction (§2.5): normalize(0.6·toward target + 0.4·attack direction), from present positions. */
function attackDir(a: Bean, tx: number, ty: number): void {
  const dx = tx - a.pos.x;
  const dy = ty - a.pos.y;
  const d = Math.hypot(dx, dy);
  const blend = TUNE.shove.dirBlend;
  const x = (d > 0 ? (blend * dx) / d : 0) + (1 - blend) * a.lungeDir.x;
  const y = (d > 0 ? (blend * dy) / d : 0) + (1 - blend) * a.lungeDir.y;
  const l = Math.hypot(x, y);
  dir.x = l > 0 ? x / l : a.lungeDir.x;
  dir.y = l > 0 ? y / l : a.lungeDir.y;
}

/** Range, arc and height test of an attack hitbox (`reach` past both radii, cosine of the half arc) against (x, y, z). */
function inArc(a: Bean, x: number, y: number, z: number, targetRadius: number, reach: number, cosArc: number, maxDz: number): boolean {
  const dx = x - a.pos.x;
  const dy = y - a.pos.y;
  const d = Math.hypot(dx, dy);
  if (d > a.radius + reach + targetRadius || Math.abs(z - a.z) > maxDz) return false;
  return d === 0 || (dx * a.lungeDir.x + dy * a.lungeDir.y) / d >= cosArc;
}

/** Height tolerance against a target: FLOAT beans can only be reached within the balloon's |Δz| (§3.3). */
function targetMaxDz(t: Bean, maxDz: number): number {
  return STATE_TABLE[t.state].grabbable === 'dz' ? Math.min(maxDz, TUNE.balloon.hitDz) : maxDz;
}

/** A bean an impulse can reach: active, in, hittable (§2.14) and not invulnerable (§2.5). */
export function canBeHit(b: Bean): boolean {
  return b.active && !b.out && isHittable(b.state) && b.invulnTicks === 0;
}

// ---- Clashes (§2.5, TUNE.clash) -------------------------------------------------------------

/**
 * Two lunges started within `clash.windowTicks` of each other, pointing at each other
 * (dot ≤ `clash.facingDot`) and each reaching the other (present positions, §20.9) CLANG: both
 * lunges end, both bounce apart at `clash.kb` (× own Dizzy mult × the other's kbDealt × own
 * kbTaken × kbMult; no tumble, no Dizzy), both shove cooldowns become `clash.cooldownTicks`, each
 * is credited to the other with cause CLASH, and CLASH carries the winner (the one knocked back
 * less, NO_SLOT when even) in `c`.
 */
function clash(w: World, a: Bean, b: Bean): void {
  let nx = b.pos.x - a.pos.x;
  let ny = b.pos.y - a.pos.y;
  const d = Math.hypot(nx, ny);
  if (d > 0) {
    nx /= d;
    ny /= d;
  } else {
    nx = a.lungeDir.x;
    ny = a.lungeDir.y;
  }
  const teammate = isTeammate(w, a.slot, b.slot);
  const base = TUNE.clash.kb * w.rules.kbMult * (teammate ? TUNE.knock.teammateMult : 1);
  const kbA = base * dizzyMult(a.dizzy) * b.kbDealt * a.kbTaken;
  const kbB = base * dizzyMult(b.dizzy) * a.kbDealt * b.kbTaken;
  bounce(w, a, b, -nx * kbA, -ny * kbA, teammate);
  bounce(w, b, a, nx * kbB, ny * kbB, teammate);
  const winner = kbA < kbB - CLASH_EVEN_EPS ? a.slot : kbB < kbA - CLASH_EVEN_EPS ? b.slot : NO_SLOT;
  emit(w, EV.CLASH, a.slot, b.slot, winner, 0, (a.pos.x + b.pos.x) / 2, (a.pos.y + b.pos.y) / 2);
}

function bounce(w: World, s: Bean, other: Bean, vx: number, vy: number, teammate: boolean): void {
  endToNormal(s);
  s.vel.x = vx;
  s.vel.y = vy;
  s.cdShove = TUNE.clash.cooldownTicks;
  s.lungeHitMask |= 1 << other.slot;
  if (!teammate) setTouch(w, s.slot, other.slot, Cause.CLASH);
}

function resolveClashes(w: World): void {
  const c = TUNE.clash;
  const s = TUNE.shove;
  for (let i = 0; i < MAX_BEANS; i++) {
    const a = w.beans[i];
    if (a.state !== BS.LUNGE || !canBeHit(a)) continue;
    for (let j = i + 1; j < MAX_BEANS; j++) {
      const b = w.beans[j];
      if (b.state !== BS.LUNGE || !canBeHit(b)) continue;
      if ((a.lungeHitMask & (1 << j)) !== 0 || (b.lungeHitMask & (1 << i)) !== 0) continue;
      if (Math.abs(a.lungeStartTick - b.lungeStartTick) > c.windowTicks) continue;
      if (a.lungeDir.x * b.lungeDir.x + a.lungeDir.y * b.lungeDir.y > c.facingDot) continue;
      if (!inArc(a, b.pos.x, b.pos.y, b.z, b.radius, s.reach, SHOVE_COS, s.maxDz)) continue;
      if (!inArc(b, a.pos.x, a.pos.y, a.z, a.radius, s.reach, SHOVE_COS, s.maxDz)) continue;
      clash(w, a, b);
      break;
    }
  }
}

// ---- Shove and punch hits -------------------------------------------------------------------

/** Hits found this tick, applied after every attacker was tested (simultaneous: a trade lands both ways). */
const pendingAttacker: number[] = [];
const pendingTarget: number[] = [];
const pendingPunch: boolean[] = [];

function collectHits(w: World, a: Bean, rewind: number, punch: boolean): void {
  const reach = punch ? TUNE.glove.reach : TUNE.shove.reach;
  const cosArc = punch ? PUNCH_COS : SHOVE_COS;
  const maxDz = punch ? TUNE.glove.maxDz : TUNE.shove.maxDz;
  for (const t of w.beans) {
    const bit = 1 << t.slot;
    if (t === a || (a.lungeHitMask & bit) !== 0 || !canBeHit(t)) continue;
    const p = hitTestPosition(w, t, rewind);
    if (!inArc(a, p.x, p.y, p.z, t.radius, reach, cosArc, targetMaxDz(t, maxDz))) continue;
    a.lungeHitMask |= bit;
    pendingAttacker.push(a.slot);
    pendingTarget.push(t.slot);
    pendingPunch.push(punch);
  }
  const kb = (punch ? TUNE.glove.kb : TUNE.shove.kb) * a.kbDealt;
  for (const e of w.ents) {
    if (!isProp(e) || propHidden(e)) continue;
    const bit = 1 << (PROP_MASK_SHIFT + e.data);
    if ((a.lungeHitMask & bit) !== 0 || !inArc(a, e.pos.x, e.pos.y, e.z, propRadius(e), reach, cosArc, maxDz)) continue;
    a.lungeHitMask |= bit;
    attackDir(a, e.pos.x, e.pos.y);
    knockProp(w, e, dir.x, dir.y, kb, e.kind === EK.DUMMY ? (punch ? TUNE.glove.dizzy : TUNE.shove.dizzy) : 0);
  }
}

function applyPendingHits(w: World): void {
  for (let i = 0; i < pendingAttacker.length; i++) {
    const a = w.beans[pendingAttacker[i]];
    const t = w.beans[pendingTarget[i]];
    const punch = pendingPunch[i];
    attackDir(a, t.pos.x, t.pos.y);
    applyKnockback(w, t.slot, dir.x, dir.y, (punch ? TUNE.glove.kb : TUNE.shove.kb) * a.kbDealt, {
      dizzy: punch ? TUNE.glove.dizzy : TUNE.shove.dizzy,
      cause: punch ? Cause.GLOVE : Cause.SHOVE,
      by: a.slot, scaleByDizzy: true, teammateCheck: true,
    });
  }
  pendingAttacker.length = 0;
  pendingTarget.length = 0;
  pendingPunch.length = 0;
}

/**
 * Step phase 8. Server: clashes first; then every active lunge and punch (its first `activeTicks`)
 * is tested against targets `ctx.rewindTicks[attacker]` ticks in the past, and all hits apply
 * together with present-position knockback; then grab checks. Predict mode: nothing resolves (a
 * local GRAB_START waits for the snapshot that decides it).
 */
export function resolveHits(w: World, ctx: StepContext): void {
  if (isPredicting(w)) {
    resolveGrabs(w, ctx);
    return;
  }
  resolveClashes(w);
  for (const a of w.beans) {
    if (!a.active) continue;
    const rewind = ctx.rewindTicks[a.slot] ?? 0;
    if (a.state === BS.LUNGE) collectHits(w, a, rewind, false);
    else if (a.state === BS.PUNCH && a.st < TUNE.glove.activeTicks) collectHits(w, a, rewind, true);
  }
  applyPendingHits(w);
  resolveGrabs(w, ctx);
}

// ---- Impulses ------------------------------------------------------------------------------

/**
 * Knocks `victim` along (dx, dy) at base speed `speed` (§2.5): × dizzyMult (if `scaleByDizzy`),
 * × airborne mult, × the victim's kbTaken, × the round's kbMult; teammates (with
 * `teammateCheck`) take half and gain no Dizzy or credit. State responses (§2.14 "Hittable"):
 * TEETER/LEDGE/CLIMB fall outward at max(kb, 3); a carrier drops its victim (with the carrier's
 * pre-hit velocity); a grabbed victim is freed; a floating bean's balloon pops and it drops in
 * TUMBLE; a yank breaks; a windup or lunge is cancelled. Dizzy, touch credit (hazards, self-hits
 * and hits without an attacker only refresh an existing credit, §2.11), the HIT event (x,y = the
 * contact point on the victim) and `MODES.onHit`. A victim `canBeHit` rejects is untouched.
 * Server-only.
 */
export function applyKnockback(w: World, victim: number, dx: number, dy: number, speed: number, o: KnockOpts): void {
  const b = w.beans[victim];
  if (isPredicting(w) || !canBeHit(b)) return;

  const teammate = o.teammateCheck === true && isTeammate(w, o.by, victim);
  const airborne = !isGrounded(b);
  let kb = speed * b.kbTaken * w.rules.kbMult;
  if (teammate) kb *= TUNE.knock.teammateMult;
  if (o.scaleByDizzy) kb *= dizzyMult(b.dizzy);
  if (airborne) kb *= TUNE.knock.airborneMult;

  const l = Math.hypot(dx, dy);
  const ux = l > 0 ? dx / l : -dcos(b.facing);
  const uy = l > 0 ? dy / l : -dsin(b.facing);

  if (!teammate) {
    addDizzy(w, b, o.dizzy);
    if (o.hazard || o.by === victim || o.by === NO_SLOT) refreshTouch(w, b, o.cause);
    else setTouch(w, victim, o.by, o.cause);
  }
  b.lastHitTick = w.tick;
  emit(w, EV.HIT, o.by, victim, o.cause, Math.round(kb * 10), b.pos.x - ux * b.radius, b.pos.y - uy * b.radius);
  MODES[w.cfg.modeId].onHit(w, o.by, victim, o.cause);

  if (STATE_TABLE[b.state].hit === 'falls') {
    forceFall(w, victim, -b.edgeNormal.x, -b.edgeNormal.y, Math.max(kb, TUNE.edge.hitFallMinSpeed));
    return;
  }

  let forceTumble = false;
  switch (b.state) {
    case BS.CARRYING: releaseCarry(w, b, 'hit'); break;
    case BS.GRABBED: freeFromCarrier(w, b); break;
    case BS.FLOAT: popFloat(w, b, o.by); forceTumble = true; break;
    case BS.YANKED: breakYank(b); break;
    case BS.LUNGE: clampLength(b.vel, b.vel.x, b.vel.y, TUNE.bean.runSpeed); break;
    default: break;
  }

  b.vel.x = ux * kb + TUNE.shove.keepVel * b.vel.x;
  b.vel.y = uy * kb + TUNE.shove.keepVel * b.vel.y;
  clampLength(b.vel, b.vel.x, b.vel.y, TUNE.collide.maxSpeed);
  if (o.vz !== undefined) b.vz = o.vz;
  else if (airborne) b.vz = Math.max(b.vz, TUNE.shove.airborneLift);

  if (forceTumble || (!o.noTumble && kb >= TUNE.knock.tumbleThreshold)) {
    b.state = BS.TUMBLE;
    b.st = 0;
  } else if (b.state === BS.LUNGE || b.state === BS.PUNCH_WINDUP || b.state === BS.CARRYING || b.state === BS.GRABBED || b.state === BS.YANKED) {
    b.state = BS.NORMAL;
    b.st = 0;
  }
}

/** Radial blast at (x, y) on the arena floor (§3.3 bomb, §4.2 potato); see `applyBlastAt`. Server-only. */
export function applyBlast(w: World, x: number, y: number, radius: number, kbCenter: number, kbEdge: number, dizzy: number, cause: Cause, by: number, maxDz: number): void {
  applyBlastAt(w, x, y, 0, radius, kbCenter, kbEdge, dizzy, cause, by, maxDz, NO_SLOT);
}

/**
 * Radial blast centred at (x, y, z) (§3.3: a bomb explodes wherever it is, in the air or in a
 * hand): beans within `radius` horizontally and `maxDz` vertically take `lerp(kbCenter, kbEdge,
 * d/radius)` away from the centre (backward from facing at d = 0), scaled by Dizzy, with Dizzy
 * and credit to `by`. `carrier` (the bean holding the bomb, or NO_SLOT) counts as d = 0 and is
 * blasted backward from its facing. Peels in the radius are destroyed; cubes, dummies and a loose
 * crown are pushed. Server-only.
 */
export function applyBlastAt(w: World, x: number, y: number, z: number, radius: number, kbCenter: number, kbEdge: number, dizzy: number, cause: Cause, by: number, maxDz: number, carrier: number): void {
  if (isPredicting(w)) return;
  const opts: KnockOpts = { dizzy, cause, by, scaleByDizzy: true, teammateCheck: true };
  for (const b of w.beans) {
    if (b.slot === carrier) {
      applyKnockback(w, b.slot, -dcos(b.facing), -dsin(b.facing), kbCenter, opts);
      continue;
    }
    const dx = b.pos.x - x;
    const dy = b.pos.y - y;
    const d = Math.hypot(dx, dy);
    if (d > radius || Math.abs(b.z - z) > maxDz) continue;
    applyKnockback(w, b.slot, dx, dy, lerp(kbCenter, kbEdge, d / radius), opts);
  }
  for (let i = w.ents.length - 1; i >= 0; i--) {
    const e = w.ents[i];
    const dx = e.pos.x - x;
    const dy = e.pos.y - y;
    const d = Math.hypot(dx, dy);
    if (d > radius || Math.abs(e.z - z) > maxDz) continue;
    if (e.kind === EK.PEEL) {
      removeEntityAt(w, i);
      continue;
    }
    const kb = lerp(kbCenter, kbEdge, d / radius);
    const ux = d > 0 ? dx / d : 1;
    const uy = d > 0 ? dy / d : 0;
    if (isProp(e) && !propHidden(e)) knockProp(w, e, ux, uy, kb, e.kind === EK.DUMMY ? dizzy : 0);
    else if (e.kind === EK.CROWN && e.data === NO_SLOT) {
      e.vel.x = ux * kb;
      e.vel.y = uy * kb;
    }
  }
}
